import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import s from "./W2Negar.module.css";
import {
  listDesviosNegarCausadores,
  listDesviosNegarPrimarias,
} from "../api";

function NegDropdown({
  value,
  onChange,
  options = [],
  placeholder = "Selecione...",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const safeOptions = useMemo(
    () =>
      (Array.isArray(options) ? options : [])
        .map((opt) => String(opt ?? "").trim())
        .filter(Boolean),
    [options]
  );

  useEffect(() => {
    if (!open) return undefined;

    const onOutside = (event) => {
      if (rootRef.current?.contains(event.target)) return;
      if (menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const onEsc = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onOutside);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) {
      setOpenUp(false);
      setMenuStyle(null);
      return undefined;
    }

    const updateDirection = () => {
      const menuGap = 2;
      const anchorEl = triggerRef.current || rootRef.current;
      if (!anchorEl) return;
      const rect = anchorEl.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const availableSpace = Math.min(
        220,
        Math.max(140, Math.floor(spaceBelow - menuGap - 8))
      );
      const top = Math.round(rect.bottom + menuGap);
      setOpenUp(false);
      setMenuStyle({
        top: `${top}px`,
        left: `${Math.round(rect.left)}px`,
        width: `${Math.round(rect.width)}px`,
        maxHeight: `${availableSpace}px`,
      });
    };

    updateDirection();
    window.addEventListener("resize", updateDirection);
    window.addEventListener("scroll", updateDirection, true);
    return () => {
      window.removeEventListener("resize", updateDirection);
      window.removeEventListener("scroll", updateDirection, true);
    };
  }, [open, safeOptions.length]);

  return (
    <div
      ref={rootRef}
      className={`${s.negSelectRoot} ${open ? s.negSelectOpen : ""} ${
        openUp ? s.negSelectOpenUp : ""
      } ${disabled ? s.negSelectDisabled : ""}`.trim()}
    >
      <button
        ref={triggerRef}
        type="button"
        className={s.negSelectTrigger}
        onClick={() => {
          if (!disabled) setOpen((prev) => !prev);
        }}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span
          className={`${s.negSelectValue} ${value ? "" : s.negSelectPlaceholder}`.trim()}
        >
          {value || placeholder}
        </span>
      </button>

      {open && menuStyle
        ? createPortal(
            <div
              ref={menuRef}
              className={`${s.negSelectMenu} ${s.negSelectMenuOpen} ${s.negSelectMenuFloating}`.trim()}
              role="listbox"
              style={menuStyle}
            >
              {safeOptions.map((opt) => {
                const selected = opt === value;
                return (
                  <button
                    key={opt}
                    type="button"
                    className={`${s.negSelectOption} ${
                      selected ? s.negSelectOptionSelected : ""
                    }`.trim()}
                    onClick={() => {
                      onChange(opt);
                      setOpen(false);
                    }}
                    aria-selected={selected}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

export default function W2Negar({
  open = false,
  onCancel = () => {},
  onConfirm = () => {},
}) {
  const [responsabilidade, setResponsabilidade] = useState("");
  const [impacto, setImpacto] = useState("");
  const [finalReason, setFinalReason] = useState("");
  const [responsabilidades, setResponsabilidades] = useState([]);
  const [impactos, setImpactos] = useState([]);
  const [loadingResponsabilidades, setLoadingResponsabilidades] = useState(false);
  const [loadingImpactos, setLoadingImpactos] = useState(false);
  const [erroResponsabilidades, setErroResponsabilidades] = useState("");
  const [erroImpactos, setErroImpactos] = useState("");

  useEffect(() => {
    let alive = true;

    if (open) {
      setResponsabilidade("");
      setImpacto("");
      setFinalReason("");
      setImpactos([]);
      setErroImpactos("");
      setErroResponsabilidades("");
      setLoadingResponsabilidades(true);

      listDesviosNegarCausadores()
        .then((rows) => {
          if (!alive) return;
          const list = Array.from(
            new Set(
              (Array.isArray(rows) ? rows : [])
                .map((r) =>
                  typeof r === "string"
                    ? r.trim()
                    : String(r?.causador ?? r ?? "").trim()
                )
                .filter(Boolean)
            )
          );
          setResponsabilidades(list);
        })
        .catch((e) => {
          if (!alive) return;
          setResponsabilidades([]);
          setErroResponsabilidades(
            e?.response?.data?.error ||
              e?.message ||
              "Erro ao carregar responsabilidades."
          );
        })
        .finally(() => {
          if (!alive) return;
          setLoadingResponsabilidades(false);
        });
    }

    return () => {
      alive = false;
    };
  }, [open]);

  useEffect(() => {
    setImpacto("");
    setImpactos([]);
    setErroImpactos("");

    if (!open || !responsabilidade) return undefined;

    let alive = true;
    setLoadingImpactos(true);

    listDesviosNegarPrimarias(responsabilidade)
      .then((rows) => {
        if (!alive) return;
        const list = Array.from(
          new Set(
            (Array.isArray(rows) ? rows : [])
              .map((r) =>
                typeof r === "string"
                  ? r.trim()
                  : String(r?.causa_primaria ?? r ?? "").trim()
              )
              .filter(Boolean)
          )
        );
        setImpactos(list);
      })
      .catch((e) => {
        if (!alive) return;
        setImpactos([]);
        setErroImpactos(
          e?.response?.data?.error || e?.message || "Erro ao carregar impactos."
        );
      })
      .finally(() => {
        if (!alive) return;
        setLoadingImpactos(false);
      });

    return () => {
      alive = false;
    };
  }, [open, responsabilidade]);

  useEffect(() => {
    const partes = [];
    if (responsabilidade) partes.push(`Responsabilidade: ${responsabilidade}`);
    if (impacto) partes.push(`Impacto: ${impacto}`);
    setFinalReason(partes.join(" | "));
  }, [responsabilidade, impacto]);

  if (!open) return null;

  const canConfirm = !!responsabilidade && !!impacto;

  const handleConfirmClick = () => {
    if (!canConfirm) return;
    onConfirm({
      causador: responsabilidade,
      causa_primaria: impacto,
      reason: finalReason,
    });
  };

  return (
    <div className={s.negBackdrop}>
      <div className={s.negCard}>
        <header className={s.negHeader}>
          <h2 className={s.negTitle}>Justificar Negativa</h2>
          <button
            type="button"
            className={s.negClose}
            aria-label="Fechar"
            onClick={onCancel}
          >
            x
          </button>
        </header>

        <div className={s.negContent}>
          <div className={s.negBox}>
            <p className={s.negText}>
              Selecione quem e o responsavel e qual o impacto principal da
              negativa desta programacao.
            </p>

            <div className={s.negGrid}>
              <div className={s.negField}>
                <span className={s.negLabel}>
                  Responsabilidade <span className={s.negRequired}>*</span>
                </span>
                <NegDropdown
                  value={responsabilidade}
                  onChange={setResponsabilidade}
                  options={responsabilidades}
                  placeholder={
                    loadingResponsabilidades
                      ? "Carregando..."
                      : responsabilidades.length
                      ? "Selecione..."
                      : "Sem opcoes cadastradas"
                  }
                  disabled={
                    loadingResponsabilidades || responsabilidades.length === 0
                  }
                />
                {!!erroResponsabilidades && (
                  <small className={s.negText}>
                    Falha ao buscar responsabilidades: {erroResponsabilidades}
                  </small>
                )}
              </div>

              <div className={s.negField}>
                <span className={s.negLabel}>
                  Impacto <span className={s.negRequired}>*</span>
                </span>
                <NegDropdown
                  value={impacto}
                  onChange={setImpacto}
                  options={impactos}
                  placeholder={
                    loadingImpactos
                      ? "Carregando..."
                      : responsabilidade
                      ? impactos.length
                        ? "Selecione..."
                        : "Sem opcoes para a responsabilidade"
                      : "Selecione a responsabilidade primeiro"
                  }
                  disabled={
                    !responsabilidade || loadingImpactos || impactos.length === 0
                  }
                />
                {!!erroImpactos && (
                  <small className={s.negText}>
                    Falha ao buscar impactos: {erroImpactos}
                  </small>
                )}
              </div>
            </div>
          </div>
        </div>

        <footer className={s.negFooter}>
          <button
            type="button"
            className={`${s.btn} ${s.btnGhost}`}
            onClick={onCancel}
          >
            Cancelar
          </button>
          <button
            type="button"
            className={`${s.btn} ${s.btnPrimary}`}
            onClick={handleConfirmClick}
            disabled={!canConfirm}
            title={
              !canConfirm
                ? "Selecione responsabilidade e impacto para confirmar"
                : "Confirmar negativa"
            }
          >
            Confirmar
          </button>
        </footer>
      </div>
    </div>
  );
}
